<template>
  <div class="h-full px-4 py-2 text-left">
    
    <!-- HEADER -->
    <div class="flex justify-between items-center mb-8 border-b pb-4">
      <div class="w-full text-left mb-4">
        <h4 class="font-bold text-xl text-gray-800">Configuration</h4>
        <p class="text-sm text-gray-500 mt-1">
          Manage your organization's lists and categories.
        </p>
      </div>

      <!-- AUTO SAVE INDICATOR -->
      <div class="min-w-max flex justify-end items-center h-10 px-4">
        <span v-if="isSaving" class="text-sm text-gray-400 font-semibold flex items-center animate-pulse">
          Saving...
        </span>
        <span v-else-if="showSaved" class="text-sm text-green-500 font-semibold flex items-center transition-opacity duration-300">
          <vs-icon icon-pack="feather" icon="icon-check" class="mr-1"></vs-icon> Saved
        </span>
      </div>
    </div>

    <!-- INITIAL LOADING -->
    <div v-if="isLoading" class="flex justify-center py-20">
      <vs-progress indeterminate color="primary" class="w-1/2"></vs-progress>
    </div>

    <!-- CONTENT -->
    <div v-else-if="localConfig">
      
      <div 
        v-for="(key, index) in displayKeys" 
        :key="key" 
        class="mb-10 w-full"
      >
        <!-- Section Header -->
        <div class="flex items-center mb-3">
          <h5 class="font-bold text-gray-700 text-lg capitalize">
            {{ localConfig[key].title }}
          </h5>
        </div>

        <!-- TYPE: DROPDOWN (Editable Chips) -->
        <div v-if="localConfig[key].type === 'dropdown'">
          
          <!-- Chips Container -->
          <div class="flex flex-wrap gap-3 mb-4 justify-start">
            <vs-chip
              v-for="(item, i) in localConfig[key].values"
              :key="i"
              v-if="item.label !== 'Add Configuration'"
              closable
              class="custom-chip"
              @click="removeTag(key, i)"
            >
              <span class="chip-text">{{ item.label }}</span>
            </vs-chip>
            
            <!-- Empty State Helper -->
            <span v-if="localConfig[key].values.length === 0" class="text-sm text-gray-400 italic py-1">
              No {{ localConfig[key].title.toLowerCase() }} yet.
            </span>
          </div>

          <!-- Add New Input Group (Left Aligned) -->
          <div class="flex items-center w-full md:w-3/4 lg:w-2/3 mt-2">
            <!-- Input Field -->
            <div class="flex-grow relative border border-gray-300 rounded-lg bg-white overflow-hidden transition-all focus-within:shadow-md">
              <vs-input 
                class="w-full input-clean"
                :placeholder="`+ Add new ${localConfig[key].title}...`" 
                v-model="inputs[key]" 
                @keyup.enter="addTag(key)"
              />
            </div>
            
            <!-- Round Plus Button -->
            <div 
              class="add-btn ml-3 shadow-md hover:shadow-lg transition transform hover:-translate-y-0.5"
              @click="addTag(key)"
            >
              <vs-icon icon="add" size="24px" color="white"></vs-icon>
            </div>
          </div>

        </div>

        <!-- TYPE: TEXTBOX (Editable Input) -->
        <div v-else-if="localConfig[key].type === 'textbox'">
          <div class="w-full md:w-1/2 relative border border-gray-300 rounded-lg bg-white overflow-hidden transition-all focus-within:shadow-md">
            <!-- Updated @input to trigger autoSave -->
            <vs-input 
              class="w-full input-clean"
              placeholder="Enter value..." 
              v-model="localConfig[key].value"
              @input="autoSave"
            />
          </div>
        </div>

        <!-- READ ONLY TYPES (Fallback) -->
        <div v-else class="p-4 bg-gray-50 rounded text-gray-500 text-sm border border-gray-100">
          <span class="font-semibold">Configuration Type:</span> {{ localConfig[key].type }}
        </div>

      </div>

    </div>

    <div v-else class="text-center mt-20 text-gray-400">
      Loading Configuration...
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'OrgSettingsConfig',
  data() {
    return {
      isLoading: false,
      isSaving: false,
      showSaved: false,
      saveTimeout: null,
      localConfig: null,
      
      displayKeys: [
        'department',
        'roleTitle',
        'level',
        'numberOfDirectReports',
        'performance',
        'potential',
        'relationship'
      ],

      inputs: {
        department: '',
        roleTitle: '',
        level: '',
        numberOfDirectReports: '',
        performance: '',
        potential: '',
        relationship: ''
      }
    };
  },
  computed: {
    ...mapState('organization', ['organizationConfiguration', 'orgData']),
  },
  watch: {
    organizationConfiguration: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          // IMPORTANT: Only populate localConfig if it hasn't been set yet.
          // This prevents the user's typing from being overwritten by the Vuex store updating mid-autosave.
          if (!this.localConfig) {
            this.localConfig = JSON.parse(JSON.stringify(newVal));

            if(this.localConfig.department) {
              this.localConfig.department.title = "Groups";
            }
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    addTag(key) {
      const val = this.inputs[key];
      if (!val || val.trim() === '') return;

      if (!this.localConfig[key].values) {
        this.$set(this.localConfig[key], 'values', []);
      }

      const newKey = val.trim().replace(/\s+/g, '').toLowerCase() + '_' + Date.now();

      this.localConfig[key].values.push({
        key: newKey,
        label: val.trim()
      });

      this.inputs[key] = '';
      this.autoSave(); // Trigger auto-save
    },

    removeTag(key, index) {
      this.localConfig[key].values.splice(index, 1);
      this.autoSave(); // Trigger auto-save
    },

    autoSave() {
      // Show saving status in UI
      this.isSaving = true;
      this.showSaved = false;
      
      // Clear any pending save requests (Debouncing)
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }

      // Wait 1 second after the user stops typing/clicking before saving
      this.saveTimeout = setTimeout(() => {
        this.saveChanges();
      }, 1000);
    },

    saveChanges() {
      if (!this.orgData || !this.orgData._id) return;

      const payload = {
        organizationId: this.orgData._id,
        configuration: this.localConfig
      };

      this.$store.dispatch('organization/updateOrganizationConfig', payload)
        .then(() => {
          // Show "Saved" temporarily
          this.showSaved = true;
          setTimeout(() => {
            this.showSaved = false;
          }, 3000); // Hide the checkmark after 3 seconds
        })
        .catch(err => {
          console.error(err);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to auto-save configuration',
            color: 'danger',
            icon: 'alert-circle'
          });
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    fetchConfig() {
      if (this.orgData && this.orgData._id) {
        // Initial load indication only
        this.isLoading = true;
        this.$store.dispatch('organization/fetchOrganizationConfig', {
          organizationId: this.orgData._id
        }).finally(() => {
          this.isLoading = false;
        });
      }
    }
  },
  mounted() {
    this.fetchConfig();
  }
};
</script>

<style scoped>
/* CHIP STYLING */
.custom-chip {
  background: white !important;
  border: 1px solid rgba(var(--vs-primary), 0.3) !important;
  height: 36px;
  border-radius: 12px !important;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  padding: 0 10px;
}

.custom-chip:hover {
  background: rgba(var(--vs-primary), 0.25) !important;
}

.chip-text {
  color: rgba(var(--vs-primary), 1);
  font-weight: 700;
  font-size: 0.95rem;
  padding-right: 5px;
}

/* 
  CLOSE (X) ICON STYLING 
  Forces the color to be Primary Background / White Icon 
*/
::v-deep .custom-chip .vs-chip--close {
  /* Default: Primary Yellow Background, White X */
  background-color: rgba(var(--vs-primary), 1) !important;
  color: white !important;
  opacity: 1 !important; /* Force override grey opacity */
  
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  transform: scale(0.9);
  transition: all 0.2s ease-in-out;
}

/* Ensure the inner icon inherits color properly */
::v-deep .custom-chip .vs-chip--close .vs-icon {
  color: inherit !important;
  font-size: 14px !important;
}

/* Hover State: FLIP Colors */
::v-deep .custom-chip .vs-chip--close:hover {
  background-color: white !important;
  color: white !important;
  
  transform: scale(1.1);
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}


/* INPUT STYLING */
::v-deep .input-clean .vs-input--input {
  border: none !important;
  background: transparent !important;
  padding: 12px 16px !important;
  box-shadow: none !important;
  font-size: 1rem;
  color: #4a5568;
  text-align: left !important;
}

::v-deep .input-clean .vs-input--placeholder {
  text-align: left !important;
  color: #a0aec0;
}

/* ROUND PLUS BUTTON */
.add-btn {
  width: 46px; 
  height: 46px; 
  min-width: 46px;
  border-radius: 50%; 
  background: rgba(var(--vs-primary), 1); 
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.add-btn:hover {
  background: rgba(var(--vs-primary), 0.9);
}
</style>